import React, { useState } from 'react';
import { api } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

export default function SubscribeWidget() {
  const { locale } = useLanguage();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [showPhone, setShowPhone] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const t = {
    title: locale === 'zh' ? '订阅通知' : 'Stay Updated',
    subtitle: locale === 'zh' ? '订阅我们的邮件列表，获取最新活动和资讯' : 'Subscribe to get notified about new events and articles',
    emailPlaceholder: locale === 'zh' ? '您的邮箱地址' : 'Your email address',
    phonePlaceholder: locale === 'zh' ? '手机号码（选填，接收短信）' : 'Phone number (optional, for SMS)',
    addPhone: locale === 'zh' ? '+ 添加手机号接收短信' : '+ Add phone for SMS alerts',
    subscribe: locale === 'zh' ? '订阅' : 'Subscribe',
    subscribing: locale === 'zh' ? '提交中...' : 'Subscribing...',
    success: locale === 'zh' ? '订阅成功！感谢您的关注。' : 'Subscribed! You\'ll receive notifications for new posts.',
    error: locale === 'zh' ? '订阅失败，请稍后再试。' : 'Failed to subscribe. Please try again.',
    invalidEmail: locale === 'zh' ? '请输入有效的邮箱地址' : 'Please enter a valid email address',
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus('error');
      setMessage(t.invalidEmail);
      return;
    }

    setStatus('loading');
    try {
      await api.post('/subscribers', {
        email: trimmed,
        phone: phone.trim() || undefined,
      });
      setStatus('success');
      setMessage(t.success);
      setEmail('');
      setPhone('');
    } catch {
      setStatus('error');
      setMessage(t.error);
    }
  };

  if (status === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.successBox}>
          <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>&#10003;</div>
          <p style={{ margin: 0, fontWeight: 600 }}>{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <h3 style={styles.title}>{t.title}</h3>
        <p style={styles.subtitle}>{t.subtitle}</p>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputRow}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              style={styles.input}
              required
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              style={{
                ...styles.button,
                opacity: status === 'loading' ? 0.7 : 1,
              }}
            >
              {status === 'loading' ? t.subscribing : t.subscribe}
            </button>
          </div>
          {!showPhone ? (
            <button
              type="button"
              onClick={() => setShowPhone(true)}
              style={styles.addPhoneBtn}
            >
              {t.addPhone}
            </button>
          ) : (
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder={t.phonePlaceholder}
              style={{ ...styles.input, marginTop: 8 }}
            />
          )}
          {status === 'error' && (
            <p style={styles.errorText}>{message}</p>
          )}
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '40px 0',
  },
  inner: {
    maxWidth: 560,
    margin: '0 auto',
    textAlign: 'center',
    padding: '32px',
    background: 'var(--white)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)',
    border: '1px solid var(--gray-200)',
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 800,
    color: 'var(--gray-900)',
    margin: '0 0 6px',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--gray-600)',
    margin: '0 0 20px',
  },
  form: {
    textAlign: 'left',
  },
  inputRow: {
    display: 'flex',
    gap: 8,
  },
  input: {
    flex: 1,
    padding: '11px 14px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--gray-300)',
    fontSize: '0.9rem',
    outline: 'none',
  },
  button: {
    padding: '11px 24px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'var(--red-primary)',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  addPhoneBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--gray-500)',
    fontSize: '0.8rem',
    cursor: 'pointer',
    marginTop: 8,
    padding: 0,
  },
  errorText: {
    color: 'var(--red-primary)',
    fontSize: '0.8rem',
    margin: '8px 0 0',
  },
  successBox: {
    maxWidth: 560,
    margin: '0 auto',
    textAlign: 'center',
    padding: '32px',
    background: 'var(--green-bg)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--green-accent)',
  },
};
